import cookie from '@tencent/vft-common/cookie';
import env from '@tencent/vft-common/env';

import { ALL_LOGIN_TYPES, COOKIE_EXPIRED_TIME, FAMILY_ROLE, LOGIN_TYPE, USERINFO_COOKIE, VIDEO_COOKIES } from './config';
import IframeCookieLoader from './IframeCookieLoader';

// 删除cookie
function removeCookie(key, domain) {
  cookie.remove(key, {
    domain,
    path: '/',
  });
  // 避免cookie有种在没domain或者没路径的域名，导致无法清cookie
  cookie.remove(key, {
    domain,
    path: '',
  });
  cookie.remove(key, {
    path: '/',
  });
}

// 清除用户信息相关污染的cookie，没domain或没路径的cookie
export function clearErrorCookies() {
  const domain = getDomain();
  // 清除上次续期的时间
  USERINFO_COOKIE.concat(['last_refresh_time', 'last_refresh_vuserid']).forEach((key) => {
    cookie.remove(key, {
      domain,
      path: '',
    });
    cookie.remove(key, {
      path: '/',
    });
  });
}

/**
 * 获取当前需要同步 cookie 的域名
 * @returns {String}
 */
export function getDomain() {
  const domain = location.host;

  // 支持域名：(*.)?film.qq.com, (*.)?v.qq.com, *.video.qq.com, 客服的域名
  if (!/(film|v|kf|kftest)\.qq\.com$/.test(domain) && !/\.video\.qq\.com$/.test(domain)) {
    return '';
  }

  const arr = domain.split('.');
  const len = arr.length;

  if (len <= 3 || /\.video\.qq\.com$/.test(domain)) {
    return domain;
  }

  return arr.slice(len - 3, len).join('.');
}

/**
 * 清除所有视频 cookie
 */
export function clearVideoCookies(domain = 'qq.com') {
  VIDEO_COOKIES.forEach((key) => {
    removeCookie(key, domain);
  });
}

/**
 * 清除 qq.com 域下的 cookie 信息
 * 一般用于非 app 场景，如果存在 qq.com 的 cookie，会导致与 video.qq.com 源 cookie 冲突
 */
export function clearRootDomainCookies() {
  clearVideoCookies('qq.com');
}

/**
 * 清除video.qq.com域下cookie
 */
export async function clearVideoDomainCookies() {
  return IframeCookieLoader.getInstance().clearVideoCookie()
    .catch(() => null);
}

/**
 * 清除当前域下的 cookie 信息
 */
export function clearPageCookies() {
  const domain = getDomain();
  const isFilmVideoDomain = /film\.video\.qq\.com/.test(domain);
  if (isFilmVideoDomain || !domain || !/([^.]+\.){2,}qq\.com/.test(location.hostname)) {
    return;
  }
  VIDEO_COOKIES.forEach((key) => {
    removeCookie(key, domain);
  });
}

// 清除刷票cache
export function clearRefreshTimeCookies() {
  // 清除上次续期的时间
  removeCookie('last_refresh_time', getDomain());
  removeCookie('last_refresh_vuserid', getDomain());
}

export function clearUserCookies() {
  const domain = getDomain();
  USERINFO_COOKIE.forEach((key) => {
    removeCookie(key, domain);
  });
}

/**
 * 判断当前是否是 Native 登录环境
 * @returns {Boolean}
 */
export function isNativeLogin() {
  return /video_platform=[3-6]/.test(document.cookie) || env.isTenvideoUnion;
}

/**
 * 获取登录态类型
 */
export function loginType() {
  const loginType = cookie.get('main_login');

  if (loginType === 'none') {
    return '';
  }

  return loginType || '';
}

/**
 * 是否是新的互联登录态
 */
export function isNewQQLogin() {
  if (loginType() === LOGIN_TYPE.wx) return false;
  if (loginType() === LOGIN_TYPE.qq && cookie.get('vqq_appid') && cookie.get('vqq_openid') && cookie.get('vqq_access_token')) {
    return true;
  }
  return false;
}

/**
 * 是否已经登录
 * 不仅单纯仅判断是否有 main_login 就代表已登录
 * 通过 cookie 中是否存在登录态对应的存在关键信息来决定
 * @param {String} type 可获取指定登录态类型的登录态
 */
export function isLogin(type) {
  if (!type) type = loginType();

  // 家庭账号子身份，type和主身份一致
  if (ALL_LOGIN_TYPES.includes(type) && getFamilyRole() === FAMILY_ROLE.child) {
    return isFamilyChildLogin();
  }

  const vuserid = isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
  const vusession = isNewQQLogin() ? cookie.get('vqq_vusession') : cookie.get('vusession');

  // cookie 中的 type 可能返回 none
  if (!type || type === 'none' || !vuserid || !vusession) {
    // 不存在视频票据时未登录
    return false;
  }

  if (type === LOGIN_TYPE.wx) {
    return isWXLogin();
  }
  if (type === LOGIN_TYPE.qq) {
    return isQQLogin();
  }
  if (type === LOGIN_TYPE.phone) {
    return isPhoneLogin();
  }

  return true;
}

/**
 * 判断是否微信登录态
 */
export function isWXLogin() {
  if (loginType() === LOGIN_TYPE.wx && cookie.get('appid') && cookie.get('openid') && cookie.get('access_token')) {
    return true;
  }
  return false;
}

/**
 * 判断是否 QQ 登录态
 */
export function isQQLogin() {
  return isPTLogin() || isNewQQLogin();
}

/**
 * 是否是旧版 PTLogin 登录态
 */
export function isPTLogin() {
  if (loginType() === LOGIN_TYPE.wx) return false;
  if (!isNewQQLogin() && ((cookie.get('uin') && cookie.get('skey'))
    || (cookie.get('luin') && cookie.get('lskey')))) {
    return true;
  }
  return false;
}

/**
 * 判断是否手机号登录态
 */
export function isPhoneLogin() {
  if (loginType() === LOGIN_TYPE.phone && cookie.get('openid') && cookie.get('vuserid')  && cookie.get('vusession')) {
    return true;
  }
  return false;
}

/**
 * 获取家庭账号的身份
 * @returns {number}
 */
export function getFamilyRole() {
  const role = Number(cookie.get('role') || '');

  return [FAMILY_ROLE.default, FAMILY_ROLE.parent, FAMILY_ROLE.child].includes(role)
    ? role
    : FAMILY_ROLE.default;
}

/**
 * 是否家庭账号子身份
 * @returns {boolean}
 */
export function isFamilyChildLogin() {
  if (!loginType()) {
    return false;
  }

  const prefix = loginType() === LOGIN_TYPE.qq ? 'vqq_' : '';
  return Boolean(getFamilyRole() === FAMILY_ROLE.child && cookie.get(`${prefix}vuserid`) && cookie.get(`${prefix}vusession`));
}

/**
 * 获取视频 vuserid
 * @returns {string}
 */
export function getVuid() {
  return isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
}

/**
 * 获取登录态信息，去掉QQ登录态的 prefix vqq_
 * @returns
 */
export function getLoginInfo() {
  if (!isLogin()) return null;

  const type = loginType();
  const prefix = type === LOGIN_TYPE.qq ? 'vqq_' : '';

  return {
    type: loginType(),
    appid: cookie.get(`${prefix}appid`),
    openid: cookie.get(`${prefix}openid`),
    accessToken: cookie.get(`${prefix}access_token`),
    vuserid: cookie.get(`${prefix}vuserid`),
    vusession: cookie.get(`${prefix}vusession`),
  };
}

/**
 * 获取用户昵称
 * @returns {string}
 */
export function getNick() {
  if (!isLogin()) return '';
  const nick = cookie.get(`${loginType()}_nick`);
  try {
    return decodeURIComponent(nick);
  } catch (error) {
    return nick;
  }
}

/**
 * 获取用户头像
 * @returns {string}
 */
export function getAvatar() {
  if (!isLogin()) return '';
  try {
    return decodeURIComponent(cookie.get(`${loginType()}_head`));
  } catch (e) {
    return '';
  }
}

/**
 * 获取登录账号 id
 * @returns {string|number}
 */
export function getUid() {
  const type = loginType();
  if (!type) return '';
  if (type === LOGIN_TYPE.qq) {
    return isNewQQLogin() ? cookie.get('vqq_openid') : '';
  }
  return cookie.get('openid');
}

/**
 * 获取QQ号
 * @deprecated
 * @returns {number}
 */
export function getUin() {
  if (getSkey() === '') {
    return 0;
  }
  let uin = parseInt(cookie.get('uin').replace(/^o0*/g, ''), 10);
  if (!uin || uin < 10000) {
    uin = parseInt(cookie.get('luin').replace(/^o0*/g, ''), 10);
    if (!uin || uin < 10000) {
      return 0;
    }
  }
  return uin;
}

/**
 * 获取 skey
 * @returns {string} skey
 * @deprecated
 */
export function getSkey() {
  const skey = cookie.get('skey') || cookie.get('lskey');
  return skey ? skey.trim() : '';
}

/**
 * 获取 PTLogin token
 * @returns {string}
 * @deprecated
 */
export function getToken() {
  const skey = getSkey();
  return skey ? time33(skey) : '';
}

/**
 * 哈希time33算法
 */
export function time33(skey) {
  let hash = 5381;
  for (let i = 0, len = skey.length; i < len; ++i) {
    hash += (hash << 5) + skey.charAt(i).charCodeAt();
  }
  return hash & 0x7fffffff;
}

/**
 * 设置用户信息到 Cookie 里
 * @param {*} userInfo
 * @returns
 */
export function setUserInfo(userInfo = {}, type) {
  if (!userInfo) return;
  const options = {
    domain: getDomain(),
    expires: COOKIE_EXPIRED_TIME,
    path: '/',
  };
  if (options.domain) {
    if (!type) {
      type = loginType();
    }
    cookie.set(`${type}_nick`, encodeURIComponent(userInfo.nick), options);
    cookie.set(`${type}_head`, encodeURIComponent(userInfo.head), options);
  }
}

export function generateGuid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  return [s4(), s4(), s4(), s4()].join('');
}
